import { useRef, useState } from 'react';
import { useOnClickOutside } from '@jywer/ui';
import { patchRate } from './api';
import { TRIGGER_EVENTS, TRIGGER_LABEL, type RateRow, type TriggerEvent } from './types';

/**
 * V1.4 scope Y — segmented control at the top of the popover:
 *   Ved indgåelse · Ved opstart · Ved levering · Custom
 *
 * The first 3 map to the corresponding TriggerEvent enum values. "Custom"
 * covers everything else (design_godkendt, sidste_revision, ved_revision,
 * faktureret, optagelsesdagen, custom) — when selected we render a small
 * native dropdown so admins keep access to the full enum.
 *
 * When trigger_event = 'ved_indgaaelse' a banner explains the
 * fire-at-register_order behaviour.
 */

interface SegmentedOption {
  key: TriggerEvent | 'custom';
  label: string;
}

const SEGMENTED: SegmentedOption[] = [
  { key: 'ved_indgaaelse', label: 'Ved indgåelse' },
  { key: 'opstartsmoede', label: 'Ved opstart' },
  { key: 'levering', label: 'Ved levering' },
  { key: 'custom', label: 'Custom' },
];

const CUSTOM_OPTIONS: TriggerEvent[] = TRIGGER_EVENTS.filter(
  (t) => t !== 'ved_indgaaelse' && t !== 'opstartsmoede' && t !== 'levering',
);

function resolveSegmented(trigger: TriggerEvent): SegmentedOption['key'] {
  if (trigger === 'ved_indgaaelse' || trigger === 'opstartsmoede' || trigger === 'levering') {
    return trigger;
  }
  return 'custom';
}

/**
 * Trigger-event picker for a rate. Hosts the `trigger_event` segmented
 * control (V1.4 scope Y — merged from the standalone select) plus a
 * Custom dropdown for the long-tail enum values.
 */
export function TriggerTaskPicker({ rate, onChanged }: { rate: RateRow; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(wrapperRef, () => setOpen(false), open);

  async function setTriggerEvent(next: TriggerEvent) {
    setBusy(true);
    setErr(null);
    try {
      await patchRate(rate.id, { trigger_event: next });
      // `ved_indgaaelse` clears any existing task binding; the server
      // already handles that side-effect, but nudge UI state back in
      // sync by reloading the parent.
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Kunne ikke ændre trigger');
    } finally {
      setBusy(false);
    }
  }

  const activeSeg = resolveSegmented(rate.trigger_event);

  const boundLabel =
    rate.trigger_event === 'ved_indgaaelse'
      ? 'Fyrer ved indgåelse'
      : TRIGGER_LABEL[rate.trigger_event];

  return (
    <div ref={wrapperRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded border border-line-2 bg-paper-0 px-1.5 py-0.5 text-xs text-ink-1 hover:border-line-2"
        title="Rediger trigger-event"
      >
        {boundLabel}
      </button>
      {open ? (
        <div className="absolute z-10 mt-1 w-80 max-h-96 overflow-auto rounded border border-line-2 bg-paper-0 shadow-lg">
          {/* V1.4-Y segmented control */}
          <div className="flex gap-px border-b border-line-1 bg-paper-1 p-1 text-[11px]">
            {SEGMENTED.map((opt) => {
              const active = activeSeg === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    if (opt.key === 'custom') {
                      if (activeSeg !== 'custom') {
                        void setTriggerEvent(CUSTOM_OPTIONS[0] ?? 'custom');
                      }
                    } else {
                      void setTriggerEvent(opt.key);
                    }
                  }}
                  className={`flex-1 rounded px-1.5 py-1 text-center transition-colors ${
                    active ? 'bg-accent text-white' : 'bg-paper-0 text-ink-2 hover:bg-paper-2'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          {activeSeg === 'custom' ? (
            <div className="border-b border-line-1 px-3 py-2 text-xs">
              <label className="mb-1 block text-[10px] uppercase tracking-wide text-ink-2">
                Custom trigger-event
              </label>
              <select
                value={rate.trigger_event}
                onChange={(e) => void setTriggerEvent(e.target.value as TriggerEvent)}
                disabled={busy}
                className="w-full rounded border border-line-2 bg-paper-0 px-1 py-0.5"
              >
                {CUSTOM_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {TRIGGER_LABEL[t]}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {rate.trigger_event === 'ved_indgaaelse' ? (
            <div className="px-3 py-3 text-xs text-ink-2">
              <b className="block text-ink-1">Faktureres ved aftaleindgåelse</b>
              Raten skydes automatisk af ordre-intaken, så snart aftalen indgås.
            </div>
          ) : null}

          {err ? <div className="px-3 py-2 text-xs text-status-err">{err}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
